import { format } from 'date-fns';
/**
 * Gap threshold in minutes for merging consecutive Untis periods into one block.
 *
 * Rationale: WebUntis inserts 5–15 minute breaks between individual periods in a
 * teaching block (e.g., 3 × 45 min English with 10 min pauses). The lunch break
 * is typically ~60 minutes, splitting an otherwise same-lessonId sequence into
 * two separate blocks in the WebUntis UI. A 20-minute threshold sits safely
 * above normal inter-period breaks and well below the lunch gap.
 */
const MERGE_GAP_THRESHOLD_MINUTES = 20;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const MERGE_GAP_THRESHOLD_MS = MERGE_GAP_THRESHOLD_MINUTES * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
/**
 * Returns a day-scoped grouping key for a given lesson.
 *
 * Uses `date-fns.format` to build an ISO-style local date string
 * (`yyyy-MM-dd`) so that two lessons on the same calendar day in the
 * user's local timezone always collide in the same bucket. Using
 * `Date.toISOString().split('T')[0]` would be incorrect here because
 * it converts to UTC and could place a lesson shortly after midnight
 * local time into the previous day's bucket.
 */
const buildGroupKey = (lesson) => {
    const localDate = format(lesson.startDate, 'yyyy-MM-dd');
    return `${localDate}-${lesson.untisLessonId ?? ''}`;
};
/**
 * Builds a synthetic Lesson from a non-empty array of consecutive same-block lessons.
 * Uses the first lesson as the base (id, startDate, untisHourInDay, course, …) and
 * overrides the endDate with the LAST lesson's endDate.
 *
 * Sets `untisBlockLength` to the authoritative number of merged Untis periods so
 * the renderer can compute `lengthHours` from the real period count instead of
 * approximating from duration (which rounds incorrectly once cumulative inter-
 * period breaks exceed half a school hour — e.g. a 4-period English block
 * 08:00–11:35 = 215 min would round to `round(215/45) = 5` slots, but it's
 * only 4 actual school hours).
 *
 * Single-period blocks also get `untisBlockLength = 1` so all Untis lessons
 * consistently carry the field and the renderer can use a single code path.
 */
const buildBlockLesson = (block) => {
    const [first] = block;
    if (block.length === 1) {
        return { ...first, untisBlockLength: 1 };
    }
    const last = block[block.length - 1];
    return {
        ...first,
        endDate: last.endDate,
        untisBlockLength: block.length,
    };
};
/**
 * Merges consecutive Untis periods that share the same `untisLessonId` on the
 * same local day into a single synthetic lesson. Preserves deep-linking by
 * reusing the first lesson's `id` on the merged block — matching WebUntis'
 * behavior of sending the first period's `period_id` when a teacher clicks
 * a merged cell.
 *
 * Merge rule: Two consecutive lessons (sorted by `startDate`) are merged if
 * their gap (`next.startDate - prev.endDate`) is within [0, 20 min]. Overlapping
 * lessons or gaps > 20 min start a new block. This naturally splits the
 * lunch break (~60 min) while keeping 5–15 min inter-period breaks merged.
 *
 * Lessons without `untisLessonId` (non-Untis, manual, malformed) pass through
 * unchanged — 1 block per lesson. The transform is therefore safe to apply
 * unconditionally; the caller gates it via `useIsUntisUser()` mostly for
 * clarity and to avoid unnecessary work for non-Untis users.
 *
 * @param lessons - Lessons from the backend (as returned by `useLessonsQueryApi`).
 * @returns An array of lessons where Untis blocks are merged into one synthetic
 *   lesson each (extended `endDate`, first lesson's `id`/`startDate`/`course`/
 *   `untisHourInDay`), and non-Untis lessons pass through unchanged.
 */
export const mergeLessonsIntoBlocks = (lessons) => {
    if (lessons.length === 0) {
        return [];
    }
    // Split input: mergeable (has untisLessonId) vs. passthrough (null/undefined)
    const mergeable = [];
    const passthrough = [];
    for (const lesson of lessons) {
        if (lesson.untisLessonId === null || lesson.untisLessonId === undefined) {
            passthrough.push(lesson);
        }
        else {
            mergeable.push(lesson);
        }
    }
    if (mergeable.length === 0) {
        return passthrough;
    }
    /*
     * Group by (localDay, untisLessonId). Different days with the same
     * lessonId are kept in separate buckets — we never merge across days.
     */
    const groups = new Map();
    for (const lesson of mergeable) {
        const key = buildGroupKey(lesson);
        const bucket = groups.get(key);
        if (bucket) {
            bucket.push(lesson);
        }
        else {
            groups.set(key, [lesson]);
        }
    }
    const merged = [];
    for (const bucket of groups.values()) {
        /*
         * Chronological order within the bucket. Defensive against the backend
         * returning lessons in a non-chronological order.
         */
        bucket.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        let blockStartIdx = 0;
        for (let i = 1; i <= bucket.length; i++) {
            const isLast = i === bucket.length;
            if (isLast) {
                merged.push(buildBlockLesson(bucket.slice(blockStartIdx, i)));
                break;
            }
            const prev = bucket[i - 1];
            const next = bucket[i];
            const gapMs = next.startDate.getTime() - prev.endDate.getTime();
            /*
             * Do NOT merge:
             *   - Overlapping lessons (gapMs < 0): defensive against malformed upstream data.
             *   - Gaps above the threshold (e.g., the 60-min lunch break).
             */
            if (gapMs < 0 || gapMs > MERGE_GAP_THRESHOLD_MS) {
                merged.push(buildBlockLesson(bucket.slice(blockStartIdx, i)));
                blockStartIdx = i;
            }
        }
    }
    return [...merged, ...passthrough];
};
